import { useMutation, useQuery } from '@apollo/client';
import { EEventFormFieldType } from '__generated__/globalTypes';
import 'ag-grid-community/styles/ag-grid.css'; // Mandatory CSS required by the grid
import 'ag-grid-community/styles/ag-theme-quartz.css'; // Optional Theme applied to the grid
import {
    EventFormField,
    EventFormFields,
    S_InputField,
    S_Section,
    S_SubmitButton,
    S_TextMultiline
} from 'components/eventView/eventForm/EventFormFields';
import { EEventForm_fields } from 'graphql/fragment/__generated__/EEventForm';
import {
    UpdateEventUserByTokenMutation,
    UpdateEventUserByTokenMutationVariables
} from 'graphql/mutation/__generated__/UpdateEventUserByTokenMutation';
import { UPDATE_EVENT_USER_INFORMATION_BY_TOKEN_MUTATION } from 'graphql/mutation/guideMutation';
import {
    EventNetworkingTopicsQuery,
    EventNetworkingTopicsQueryVariables
} from 'graphql/query/__generated__/EventNetworkingTopicsQuery';
import {
    EventUserInformationByTokenQuery,
    EventUserInformationByTokenQueryVariables
} from 'graphql/query/__generated__/EventUserInformationByTokenQuery';
import {
    EVENT_NETWORKING_TOPICS_QUERY,
    EVENT_USER_INFORMATION_BY_TOKEN_QUERY
} from 'graphql/query/guideQuery';
import { EFonts, EVariables } from 'libs/styles/styling';
import { S_PageStyle } from 'pages/page/PageStyle';
import { IndustriukaLogoSvg } from 'pages/quickEdit/industriuka-logo';
import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import styled from 'styled-components/macro';

interface Props {
    className?: string;
}

export const EventUserQuickEditPage = (props: Props): JSX.Element => {
    const { eventId, userId, token } = useParams() as {
        eventId: string;
        userId: string;
        token: string;
    };
    const [isSent, setIsSent] = useState<boolean>(false);

    const networkingTopicsQuery = useQuery<
        EventNetworkingTopicsQuery,
        EventNetworkingTopicsQueryVariables
    >(EVENT_NETWORKING_TOPICS_QUERY, { variables: { eventId } });

    const userInformationQuery = useQuery<
        EventUserInformationByTokenQuery,
        EventUserInformationByTokenQueryVariables
    >(EVENT_USER_INFORMATION_BY_TOKEN_QUERY, { variables: { eventId, userId, token } });

    const [sendSubmitMutation, submitMutation] = useMutation<
        UpdateEventUserByTokenMutation,
        UpdateEventUserByTokenMutationVariables
    >(UPDATE_EVENT_USER_INFORMATION_BY_TOKEN_MUTATION);

    if (!networkingTopicsQuery.data || !userInformationQuery.data) {
        return null;
    }

    const networkingTopics = networkingTopicsQuery.data.guideView.networkingTopics;
    const userInformation = userInformationQuery.data?.e_userInformation_useToken;

    const KEYS = {
        FIRST_NAME: 'first_name',
        LAST_NAME: 'last_name',
        COMPANY_NAME: 'company_name',
        JOB_POSITION: 'job_position',
        PUBLIC_DESCRIPTION: 'public_description',
        LINKED_IN: 'linked_in',
        WEBSITE_URL: 'website_url',
        PHOTO_URL: 'photo_url',
        NETWORKING_TOPICS: networkingTopics.map((e) => e.guideNetworkingTopicId)
    };

    const fields: EventFormField[] = [
        {
            fieldId: KEYS.FIRST_NAME,
            title: 'First Name',
            text: null,
            type: EEventFormFieldType.STRING,
            placeholder: 'John',
            __typename: 'EventFormField',
            validation: {
                required: true,
                maxLength: 100,
                __typename: 'EventFormFieldValidation'
            },
            options: []
        },
        {
            fieldId: KEYS.LAST_NAME,
            title: 'Last Name',
            text: null,
            type: EEventFormFieldType.STRING,
            placeholder: 'Doe',
            __typename: 'EventFormField',
            validation: {
                required: true,
                maxLength: 100,
                __typename: 'EventFormFieldValidation'
            },
            options: []
        },
        {
            fieldId: KEYS.COMPANY_NAME,
            title: 'Company',
            text: null,
            placeholder: 'Name',
            type: EEventFormFieldType.STRING,
            __typename: 'EventFormField',
            validation: {
                required: false,
                maxLength: 100,
                __typename: 'EventFormFieldValidation'
            },
            options: []
        },
        {
            fieldId: KEYS.JOB_POSITION,
            title: 'Position',
            text: null,
            placeholder: 'Role',
            type: EEventFormFieldType.STRING,
            __typename: 'EventFormField',
            validation: {
                required: false,
                maxLength: 100,
                __typename: 'EventFormFieldValidation'
            },
            options: []
        },
        {
            fieldId: KEYS.PUBLIC_DESCRIPTION,
            title: 'Your introduction to the event',
            text: null,
            placeholder:
                "Share a bit about yourself. Who are you? What's your profession? And what are you in search of?",
            type: EEventFormFieldType.STRING_MULTILINE,
            __typename: 'EventFormField',
            validation: {
                required: false,
                maxLength: 300,
                __typename: 'EventFormFieldValidation'
            },
            options: []
        },
        {
            fieldId: KEYS.LINKED_IN,
            title: 'LinkedIn',
            text: null,
            placeholder: 'https://',
            type: EEventFormFieldType.URL,
            __typename: 'EventFormField',
            validation: {
                required: false,
                maxLength: 100,
                __typename: 'EventFormFieldValidation'
            },
            options: []
        },
        {
            fieldId: KEYS.WEBSITE_URL,
            title: 'Website',
            text: null,
            placeholder: 'https://',
            type: EEventFormFieldType.URL,
            __typename: 'EventFormField',
            validation: {
                required: false,
                maxLength: 100,
                __typename: 'EventFormFieldValidation'
            },
            options: []
        },
        {
            fieldId: KEYS.PHOTO_URL,
            title: 'Your photo',
            text: null,
            placeholder: 'https://',
            type: EEventFormFieldType.IMAGE,
            __typename: 'EventFormField',
            validation: {
                required: false,
                maxLength: null,
                __typename: 'EventFormFieldValidation'
            },
            options: []
        },
        ...networkingTopics.map<EEventForm_fields>((topic) => {
            return {
                fieldId: topic.guideNetworkingTopicId,
                title: topic.questionTitle,
                text: null,
                placeholder: null,
                validation: {
                    required: false,
                    maxLength: null,
                    __typename: 'EventFormFieldValidation'
                },
                type: EEventFormFieldType.MULTI_OPTIONS,
                options: topic.options.map((option) => ({
                    title: option.title,
                    optionId: option.optionId,
                    __typename: 'EventFormFieldOption'
                })),
                __typename: 'EventFormField'
            };
        }),
        {
            fieldId: 'agree_terms',
            title: (
                <>
                    I agree with&nbsp;
                    <a
                        href="https://mapheim.com/industriuka-terms"
                        style={{ textDecoration: 'underline' }}
                        target="_bank"
                    >
                        terms and conditions
                    </a>
                </>
            ) as unknown as string,
            text: null,
            placeholder: null,
            type: EEventFormFieldType.BOOLEAN,
            __typename: 'EventFormField',
            validation: {
                required: true,
                maxLength: null,
                __typename: 'EventFormFieldValidation'
            },
            options: []
        }
    ];

    const prefilledValues = {
        [KEYS.FIRST_NAME]: userInformation?.firstName,
        [KEYS.LAST_NAME]: userInformation?.lastName,
        [KEYS.COMPANY_NAME]: userInformation?.companyName,
        [KEYS.JOB_POSITION]: userInformation?.jobPosition,
        [KEYS.LINKED_IN]: userInformation?.linkedIn,
        [KEYS.WEBSITE_URL]: userInformation?.websiteUrl,
        [KEYS.PHOTO_URL]: userInformation?.photoUrl,
        [KEYS.PUBLIC_DESCRIPTION]: userInformation?.publicDescription,
        ...Object.fromEntries(
            networkingTopics.map((topic) => {
                return [
                    topic.guideNetworkingTopicId,
                    topic.options
                        .filter((e) =>
                            userInformation?.networkingTopicOptionIds?.includes(e.optionId)
                        )
                        ?.map((e) => e.optionId) ?? []
                ];
            })
        )
    };

    function submitUpdate(data: Record<string, unknown>) {
        sendSubmitMutation({
            variables: {
                eventId,
                userId,
                token,
                information: {
                    firstName: data[KEYS.FIRST_NAME] as string,
                    lastName: data[KEYS.LAST_NAME] as string,
                    companyName: data[KEYS.COMPANY_NAME] as string,
                    jobPosition: data[KEYS.JOB_POSITION] as string,
                    linkedIn: data[KEYS.LINKED_IN] as string,
                    websiteUrl: data[KEYS.WEBSITE_URL] as string,
                    photoUrl: data[KEYS.PHOTO_URL] as string,
                    publicDescription: data[KEYS.PUBLIC_DESCRIPTION] as string,
                    networkingTopicOptionIds: networkingTopics.flatMap((topic) => {
                        return data[topic.guideNetworkingTopicId] as string[];
                    })
                }
            },
            onCompleted: () => {
                setIsSent(true);
            }
        });
    }

    return (
        <S_EventUserQuickEditPage className={props.className}>
            <S_Body>
                <IndustriukaLogoSvg />

                {isSent ? (
                    <S_Sent>
                        <S_EventMessage>
                            <>
                                Thank you for updating your information. Please download the
                                application from the store.
                            </>
                            <S_Links>
                                <Link
                                    to="https://apps.apple.com/my/app/industriuka/id6499011134?platform=iphone"
                                    target="_blank"
                                >
                                    <S_Img src="https://dvtqxks0zalev.cloudfront.net/a2400522-8edf-4cf0-894b-aa522c6ddcb1.png" />
                                </Link>
                                <Link
                                    to="https://play.google.com/store/apps/details?id=com.mapheim.industriuka&hl=en"
                                    target="_blank"
                                >
                                    <S_Img src="https://dvtqxks0zalev.cloudfront.net/3ff86871-4138-4b05-acc0-f3a0f4bf1531.png" />
                                </Link>
                            </S_Links>
                        </S_EventMessage>
                    </S_Sent>
                ) : (
                    <>
                        <S_PageTitle>Hello {prefilledValues?.['first_name']}</S_PageTitle>
                        <S_EventMessage>
                            Please update your profile and then download the application for
                            Industriuka 2024. It will give you an opportunity to network with other
                            people, see the program, exhibitors, information and interactive map.
                        </S_EventMessage>
                        <S_EventFormFields
                            guideId={networkingTopicsQuery.data.guideView.guideId}
                            formFields={fields}
                            onSubmit={submitUpdate}
                            prefilledValues={prefilledValues}
                            isLoading={submitMutation.loading}
                            submitText="Update profile"
                        />
                    </>
                )}
            </S_Body>
        </S_EventUserQuickEditPage>
    );
};

export default EventUserQuickEditPage;

const S_EventUserQuickEditPage = styled(S_PageStyle)`
    display: flex;
    flex-direction: column;
    background: #14284f;
    width: 100%;
`;

const S_Body = styled.div`
    display: flex;
    flex-direction: column;
    padding: 20px;
    max-width: 500px;
    margin: auto;
`;

const S_EventFormFields = styled(EventFormFields)`
    width: 100%;
    margin: 0 auto 200px 0;
    ${S_Section} {
        background-color: #eaf0f5;
        ${S_InputField}, ${S_TextMultiline} {
            background-color: white;
            box-shadow: 0px 0px 20px 0px #efefef inset;
        }
    }
    ${S_SubmitButton} {
        background-color: #e36f43;
        > div {
            color: white !important;
        }
    }
`;

const S_Sent = styled.div`
    display: flex;
    justify-content: center;
    align-items: center;
    flex-direction: column;
`;

const S_EventMessage = styled.div`
    font-size: 14px;
    margin: 0 0 20px 0;
    width: 100%;
    background-color: #ffffff;
    padding: 15px 20px;
    line-height: 24px;
    border-radius: ${EVariables.Radius1};
    display: flex;
    flex-direction: column;
    @media only screen and (min-width: 1260px) {
        font-size: 15px;
        line-height: 25px;
        padding: 20px 30px;
    }
`;

const S_PageTitle = styled.div`
    color: white;
    text-align: center;
    font-family: ${EFonts.Medium};
    font-size: 30px;
    line-height: 45px;
    margin: 0 0 25px 0;
`;

const S_Img = styled.img`
    width: 130px;
    @media only screen and (min-width: 1260px) {
        width: 180px;
    }
`;

const S_Links = styled.div`
    display: flex;
    justify-content: center;
    align-items: center;
    margin-top: 20px;
    gap: 10px;
`;
